import type {PayloadAction} from '@reduxjs/toolkit'
import {subDays} from 'date-fns'
import type {SagaIterator} from 'redux-saga'
import {fork, takeLatest, select, call, put} from 'redux-saga/effects'
import {getApiUrl} from 'api'
import {dateDeforeAndAfter} from 'helpers/api'
import {sagaFetch, standardErrorAction} from 'helpers/saga'
import {toast} from 'shared/toaster'
import {updateEventStatus} from './actions'
import {
  SET_FILTERS,
  TOGGLE_HISTORIC_EVENTS,
  FETCH_REQUEST,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  UPDATE_STATUS,
  UPDATE_STATUS_REQUEST,
  UPDATE_STATUS_SUCCESS,
  UPDATE_STATUS_FAILURE,
} from './constants'
import {handleEventUpdate} from './helpers'
import type {
  FlowActionMeta,
  FlowActionUpdateStatusPayload,
  FlowStoreFilters,
} from './types'

function getFetchParams(
  filters: FlowStoreFilters,
  showHistoricEvents: boolean
) {
  let act_ids
  let tour_ids
  const startDay = subDays(new Date(), 14)
  let endDay

  if (filters.kind === 'roster') {
    // Do nothing
  } else if (filters.kind === 'represented_by_me') {
    act_ids = 'represented_by_me'
  } else if (filters.kind === 'act') {
    act_ids = filters.actId
  } else if (filters.kind === 'tour') {
    tour_ids = filters.tourId
  }

  // Handle dates
  let {before, after} = dateDeforeAndAfter({
    startDay,
    endDay,
  })

  if (filters.kind === 'tour' || showHistoricEvents) {
    before = undefined
    after = undefined
  }

  return {
    type: 'EVENT_TYPE_SHOW',
    after,
    before,
    act_ids,
    tour_ids,
  }
}

export function* fetchPlanners(
  action: PayloadAction<void, string, FlowActionMeta>
): SagaIterator {
  const {meta} = action
  yield put({type: FETCH_REQUEST, meta})
  try {
    const {filters, showHistoricEvents} = yield select(({flow}) => ({
      filters: flow[meta.flow].filters,
      showHistoricEvents: flow[meta.flow].showHistoricEvents,
    }))

    if (!filters.kind) {
      throw new Error('Filter kind needed')
    }

    const queryParams = yield call(() =>
      getFetchParams(filters, showHistoricEvents)
    )

    const payload = yield call(
      sagaFetch,
      getApiUrl('plannerEvents'),
      'get',
      null,
      {
        queryParams,
      }
    )
    yield put({type: FETCH_SUCCESS, payload, meta})
  } catch (e: any) {
    yield put(standardErrorAction(FETCH_FAILURE, e, meta))
    if (e.message === 'Filter kind needed') {
      // Ignore
    } else {
      throw e
    }
  }
}

export function* updateStatus(
  action: PayloadAction<FlowActionUpdateStatusPayload, string, FlowActionMeta>
): SagaIterator {
  const {meta} = action
  yield put({type: UPDATE_STATUS_REQUEST, meta})
  const {id, nextStatus, prevStatus} = action.payload
  try {
    yield put(updateEventStatus({id, status: nextStatus}, meta))
    const payload = yield call(handleEventUpdate, id, nextStatus, meta)
    yield put({type: UPDATE_STATUS_SUCCESS, payload, meta})
  } catch (e: any) {
    yield put(updateEventStatus({id, status: prevStatus}, meta))

    if (e.code === 403) {
      if (prevStatus === 'CONTRACT_STATUS_CONFIRMED') {
        yield call(toast.error, 'Tax-ID missing', {
          description:
            'Please check that the Invoice Organizer or the organizer has a Tax-ID',
        })
      } else {
        yield call(toast.error, 'Contract state change not allowed')
      }
    } else {
      yield call(toast.error, 'Could not update show status', {
        description: 'Please try again',
      })
    }

    yield put(standardErrorAction(UPDATE_STATUS_FAILURE, e, meta))
    throw e
  }
}

export function* flowSaga() {
  yield fork(function* () {
    yield takeLatest([SET_FILTERS, TOGGLE_HISTORIC_EVENTS], fetchPlanners)
  })
  yield fork(function* () {
    yield takeLatest(UPDATE_STATUS, updateStatus)
  })
}
